/**
 * PapergenManagement — Mode 2 Management (settings-style surface).
 *
 * Templates + Style Profiles list-and-edit. Not composition.
 * Separate from the three-pane Studio.
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Stack, Button, IconButton, TextField,
  Divider, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, ToggleButtonGroup, ToggleButton,
  LinearProgress, Alert, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarOutline as StarOutlineIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  listStyleProfiles, createStyleProfile, updateStyleProfile, deleteStyleProfile,
  listTemplates, deleteTemplate, instantiateTemplate, updateTemplate,
  getTemplate,
} from '../../api/papergen';
import { StyleProfile, PaperTemplate } from '../../types/papergen';

// ──────────────────────────────────────────────────
// StyleProfile section
// ──────────────────────────────────────────────────

const StyleProfileSection: React.FC<{ token: string | null }> = ({ token }) => {
  const [profiles, setProfiles] = useState<StyleProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProfile, setEditProfile] = useState<StyleProfile | null>(null);
  const [formName, setFormName] = useState('');
  const [formSpec, setFormSpec] = useState('{}');
  const [formDefault, setFormDefault] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await listStyleProfiles(token);
      setProfiles(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const handleOpenCreate = () => {
    setEditProfile(null);
    setFormName('');
    setFormSpec('{\n  "para_body": { "family": "sans", "size": "body" }\n}');
    setFormDefault(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (p: StyleProfile) => {
    setEditProfile(p);
    setFormName(p.name);
    setFormSpec(JSON.stringify(p.spec, null, 2));
    setFormDefault(p.is_default);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!token) return;
    try {
      const spec = JSON.parse(formSpec);
      if (editProfile) {
        await updateStyleProfile(editProfile.id, {
          name: formName, spec, is_default: formDefault,
        }, token);
      } else {
        await createStyleProfile({ name: formName, spec, is_default: formDefault }, token);
      }
      setDialogOpen(false);
      load();
    } catch (e) { alert('Invalid JSON spec'); }
  };

  const handleDelete = async (id: number) => {
    if (!token || !window.confirm('Delete this style profile?')) return;
    await deleteStyleProfile(id, token);
    load();
  };

  const handleSetDefault = async (p: StyleProfile) => {
    if (!token) return;
    await updateStyleProfile(p.id, { is_default: !p.is_default }, token);
    load();
  };

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>Style Profiles</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          New Profile
        </Button>
      </Stack>

      {loading ? <LinearProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Default</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles.map(p => (
                <TableRow key={p.id}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>v{p.version}</TableCell>
                  <TableCell>
                    <Tooltip title={p.is_default ? 'Default' : 'Set as default'}>
                      <IconButton size="small" onClick={() => handleSetDefault(p)}>
                        {p.is_default ? <StarIcon color="warning" /> : <StarOutlineIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{p.school_id ? `School ${p.school_id}` : 'Global'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenEdit(p)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(p.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editProfile ? 'Edit' : 'Create'} Style Profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth value={formName}
              onChange={e => setFormName(e.target.value)} />
            <FormControl>
              <Typography variant="caption">Spec (JSON — 14-token vocabulary)</Typography>
              <TextField
                multiline minRows={6} fullWidth
                value={formSpec}
                onChange={e => setFormSpec(e.target.value)}
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
              />
            </FormControl>
            <Button
              variant={formDefault ? 'contained' : 'outlined'}
              onClick={() => setFormDefault(!formDefault)}
              size="small"
            >
              {formDefault ? 'Default Profile ✓' : 'Is Default?'}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ──────────────────────────────────────────────────
// Templates section
// ──────────────────────────────────────────────────

const TemplatesSection: React.FC<{ token: string | null }> = ({ token }) => {
  const [templates, setTemplates] = useState<PaperTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [instDialogOpen, setInstDialogOpen] = useState(false);
  const [instTemplateId, setInstTemplateId] = useState<number | null>(null);
  const [instTitle, setInstTitle] = useState('');
  const [instGrade, setInstGrade] = useState(11);
  const [instSubject, setInstSubject] = useState('');
  const [instSubmitting, setInstSubmitting] = useState(false);

  // Edit template state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTemplateId, setEditTemplateId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editNumbering, setEditNumbering] = useState('[]');
  const [editChrome, setEditChrome] = useState('{}');
  const [editMarksPosition, setEditMarksPosition] = useState('heading');

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await listTemplates(token);
      setTemplates(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const handleDelete = async (id: number) => {
    if (!token || !window.confirm('Delete this template?')) return;
    await deleteTemplate(id, token);
    load();
  };

  const handleOpenInstantiate = (t: PaperTemplate) => {
    setInstTemplateId(t.id);
    setInstTitle('');
    setInstGrade(11);
    setInstSubject('');
    setInstDialogOpen(true);
  };

  const handleInstantiate = async () => {
    if (!token || !instTemplateId) return;
    setInstSubmitting(true);
    try {
      await instantiateTemplate(instTemplateId, {
        title: instTitle || 'New Paper',
        grade: instGrade,
        subject: instSubject || undefined,
      }, token);
      setInstDialogOpen(false);
      alert('Workspace created! Switch to Studio to compose.');
    } catch (e: any) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally { setInstSubmitting(false); }
  };

  const handleOpenEdit = (t: PaperTemplate) => {
    setEditTemplateId(t.id);
    setEditName(t.name);
    setEditNumbering(JSON.stringify(t.numbering_scheme, null, 2));
    const chrome = t.chrome || {};
    setEditChrome(JSON.stringify(chrome, null, 2));
    setEditMarksPosition(chrome.marks_position || 'heading');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!token || !editTemplateId) return;
    try {
      const numbering = JSON.parse(editNumbering);
      const chrome = JSON.parse(editChrome);
      // Merge marks_position into chrome (UI convenience)
      chrome.marks_position = editMarksPosition;
      await updateTemplate(editTemplateId, {
        name: editName,
        numbering_scheme: numbering,
        chrome,
      }, token);
      setEditDialogOpen(false);
      load();
    } catch (e: any) {
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <>
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Paper Templates</Typography>

      {loading ? <LinearProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Style Profile ID</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.id}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>v{t.version}</TableCell>
                  <TableCell><Chip label={t.scope} size="small" /></TableCell>
                  <TableCell>{t.style_profile_id}</TableCell>
                  <TableCell align="right">
                    {/* C2: Instantiate workspace */}
                    <Button size="small" onClick={() => handleOpenInstantiate(t)} sx={{ mr: 0.5 }}>
                      New Paper
                    </Button>
                    <IconButton size="small" onClick={() => handleOpenEdit(t)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(t.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {templates.length === 0 && (
                <TableRow><TableCell colSpan={5}>No templates. Save a workspace as a template from the Studio.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Instantiate dialog */}
      <Dialog open={instDialogOpen} onClose={() => setInstDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Paper from Template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth size="small"
              value={instTitle} onChange={e => setInstTitle(e.target.value)}
              placeholder="e.g. Grade 11 CS Paper II — June 2025" />
            <TextField label="Grade" type="number" size="small"
              value={instGrade} onChange={e => setInstGrade(Number(e.target.value))} />
            <TextField label="Subject" size="small"
              value={instSubject} onChange={e => setInstSubject(e.target.value)}
              placeholder="e.g. Computer Science" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInstantiate} variant="contained" disabled={instSubmitting}>
            {instSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit template dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth size="small"
              value={editName} onChange={e => setEditName(e.target.value)} />
            <Box>
              <Typography variant="caption">Numbering Scheme (JSON)</Typography>
              <TextField multiline minRows={4} fullWidth size="small"
                value={editNumbering} onChange={e => setEditNumbering(e.target.value)}
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
            </Box>
            <Box>
              <Typography variant="caption">Chrome (JSON — covers, headers, footers)</Typography>
              <TextField multiline minRows={3} fullWidth size="small"
                value={editChrome} onChange={e => setEditChrome(e.target.value)}
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
            </Box>
            <FormControl size="small">
              <InputLabel>Marks Position</InputLabel>
              <Select value={editMarksPosition} label="Marks Position"
                onChange={e => setEditMarksPosition(e.target.value)}>
                <MenuItem value="heading">Next to question number (default)</MenuItem>
                <MenuItem value="after_answers">Below answer lines</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ──────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────

const PapergenManagement: React.FC = () => {
  const { token } = useAuth();

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>Papergen — Style Profiles &amp; Templates</Typography>
      <Divider sx={{ mb: 3 }} />

      <StyleProfileSection token={token} />
      <TemplatesSection token={token} />
    </Box>
  );
};

export default PapergenManagement;
